import {html} from 'lit-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {live} from 'lit-html/directives/live';
import {classMap} from 'lit-html/directives/class-map';

import TemplateRegistry from '../template/template-registry';
import {optionClassMap} from '../template/template-util.js';
import Util from '../util';

class QuantityController {

    constructor() {
        this.config = null;
        this.quantity = 1;
        this.themeQuantityEl = null; // theme's original input (hidden, kept for sync)
        this.ownQuantityEl = null;   // our rendered input
        this._initOwnEl = null;     // bound listener ref for cleanup
    }

    init(option) {

        const min = parseInt(option.params.min) || 1;

        this.findThemeEl();

        const initVal = parseInt(option.value);
        const themeVal = this.themeQuantityEl ? parseInt(this.themeQuantityEl.value) : NaN;

        if (themeVal > 1 && themeVal >= min) {
            // theme has a meaningful quantity that meets min
            this.quantity = themeVal;
        } else if (initVal && initVal >= min) {
            // prefer config initial value over min
            this.quantity = initVal;
        } else {
            this.quantity = min;
        }

        option.value = String(this.quantity);

        // remove previous listener if re-initializing (e.g. editor mode)
        if(this._initOwnEl){
            this.config.productForm.removeEventListener('options:rendered', this._initOwnEl);
        }

        // grab our own rendered input after first render
        this._initOwnEl = () => {
            this.config.productForm.removeEventListener('options:rendered', this._initOwnEl);
            this._initOwnEl = null;
            this.ownQuantityEl = document.querySelector('#' + option.id + '-quantity');
            if(this.ownQuantityEl){
                this.ownQuantityEl.value = String(this.quantity);
            }
        };
        this.config.productForm.addEventListener('options:rendered', this._initOwnEl);
    }

    findThemeEl(){
        if(this.config.editorMode){
            return; // avoid manipulating theme elements in editor mode
        }

        const selector = '[name=quantity]';

        this.themeQuantityEl = this.config.productForm.querySelector(selector) || document.querySelector(selector);
        if(!this.themeQuantityEl) return;
        // hide and remove name so our input takes over form submission
        this.themeQuantityEl.removeAttribute('name');
        this.themeQuantityEl.style.display = 'none';

        // hide linked label if found
        if(this.themeQuantityEl.id){
            const label = document.querySelector('label[for="' + this.themeQuantityEl.id + '"]');
            if(label) label.style.display = 'none';
        }
        // hide theme specific <quantity> web component wrapper if it exists (e.g. Dawn)
        if(this.themeQuantityEl.parentElement?.tagName.includes('QUANTITY')) {
            this.themeQuantityEl.parentElement.style.display = 'none';
        }
    }

    syncEl() {
        if(this.ownQuantityEl) this.ownQuantityEl.value = this.quantity;

        // check if theme replaced the quantity element (some themes dynamically rebuild blocks)
        if(this.themeQuantityEl && !Util.isInPage(this.themeQuantityEl)){
            this.findThemeEl();
        }

        // sync to theme's input (theme JS may hold a reference to it)
        if(this.themeQuantityEl) this.themeQuantityEl.value = this.quantity;
    }

    syncValue(option) {
        if(!this.ownQuantityEl) return;

        let val = parseInt(this.ownQuantityEl.value) || 1;

        // clamp to min/max
        const min = parseInt(option.params.min) || 1;
        const max = parseInt(option.params.max) || Number.MAX_SAFE_INTEGER;
        val = Math.max(min, Math.min(max, val));

        this.quantity = val;
        option.value = String(this.quantity);

        this.syncEl();
    }

    increment(option) {
        const min = parseInt(option.params.min) || 1;
        const max = parseInt(option.params.max) || Number.MAX_SAFE_INTEGER;
        const step = parseInt(option.params.step) || 1;
        // round up to next step boundary from min
        const stepsFromMin = Math.floor((this.quantity - min) / step);
        this.quantity = Math.min(min + (stepsFromMin + 1) * step, max);
        option.value = String(this.quantity);
        this.syncEl();
    }

    decrement(option) {
        const min = parseInt(option.params.min) || 1;
        const step = parseInt(option.params.step) || 1;
        // round down to previous step boundary from min
        const stepsFromMin = Math.ceil((this.quantity - min) / step);
        this.quantity = Math.max(min + (stepsFromMin - 1) * step, min);
        option.value = String(this.quantity);
        this.syncEl();
    }
}


TemplateRegistry.templates['quantity'] = (option, config, onChange) => {

    let init = option._controller === undefined;

    option._controller = option._controller || new QuantityController();
    option._controller.config = config;

    if(init || config.editorMode){
        option._controller.init(option);
    }

    // set initial value if not yet set
    if(!option.value){
        option.value = String(option._controller.quantity);
    }

    // always render our own +/- quantity input
    return html`
        <div class="cl-po--quantity-wrap">
            <span class="cl-po--quantity-btn" @click=${() => { option._controller.decrement(option); onChange(option); }}>-</span>
            <input type="number" class="cl-po--input cl-po--quantity ${classMap(optionClassMap(option))}" id="${option.id}-quantity"
                   name="quantity" .value=${live(String(option._controller.quantity))}
                   min=${ifDefined(option.params.min || 1)} max=${ifDefined(option.params.max)}
                   @change=${() => { option._controller.syncValue(option); onChange(option); }}
                   form=${ifDefined(config.productFormLink)}>
            <span class="cl-po--quantity-btn" @click=${() => { option._controller.increment(option); onChange(option); }}>+</span>
        </div>
    `;
};
